import React from 'react'
import { styled, Typography as MIUTypography, IconButton, Tooltip, CircularProgress } from '@mui/material'
import { CloudDownload } from '@mui/icons-material'
import MUIDataTable from 'mui-datatables'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { columns } from './columns'
import './index.css'

const CustomMUIDatatable = styled(MUIDataTable)(
  ({ theme }) => `
  padding: ${theme.spacing(0, 1)};
  height: 100%;
  display: flex;
  flex-direction: column;

  .MuiPaper-root {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .MUIDataTable-root {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .MUIDataTable-responsiveBase {
    flex: 1;
    overflow: auto;
  }
`
)

const Typography = styled(MIUTypography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1.1em',
  fontWeight: 650,
  paddingBottom: 6,
  paddingTop: 6,
}))

const getMuiTheme = () =>
  createTheme({
    components: {
      MUIDataTableHeadCell: {
        styleOverrides: {
          data: {
            fontSize: '.9em',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#011339',
          },
        },
      },
      MUIDataTable: {
        styleOverrides: {
          root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          paper: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          root: {
            flexShrink: 0,
          },
        },
      },
      MUIDataTableBodyRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
    },
  })

const EmptyState = ({ message }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      fontSize: '1.2em',
      color: '#666',
    }}>
    {message}
  </div>
)

export default function GenerateReportComponent({
  headers,
  reportData,
  selectedTenant,
  page,
  rowsPerPage,
  onTableChange,
  onDownloadAll,
  downloading,
}) {
  if (!selectedTenant) {
    return <EmptyState message='Selecciona un tenant para cargar el reporte' />
  }

  if (!reportData?.resultALL || reportData.resultALL.length === 0) {
    return <EmptyState message='No hay datos disponibles para este tenant' />
  }

  const options = {
    serverSide: true,
    count: reportData?.pagination?.total ?? 0,
    page,
    rowsPerPage,
    onTableChange,
    search: false,
    print: false,
    filter: false,
    sort: false,
    viewColumns: true,
    download: false,
    customToolbar: () => (
      <Tooltip title='Descargar todo (Excel)'>
        <span>
          <IconButton onClick={onDownloadAll} disabled={downloading}>
            {downloading ? <CircularProgress size={22} /> : <CloudDownload />}
          </IconButton>
        </span>
      </Tooltip>
    ),
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: 'none',
    responsive: 'standard',
    elevation: 1,
    fixedHeader: true,
    fixedSelectColumn: false,
    textLabels: {
      pagination: {
        rowsPerPage: 'Filas por página:',
        displayRows: 'de',
      },
    },
  }

  return (
    <ThemeProvider theme={getMuiTheme()}>
      <CustomMUIDatatable
        title={
          <Typography sx={{ fontSize: '1.2em', pt: 5 }}>
            Árboles — {selectedTenant.fullName || selectedTenant.name}
          </Typography>
        }
        data={reportData.resultALL}
        columns={columns}
        options={options}
      />
    </ThemeProvider>
  )
}
